<template>
<div>
  <div class="container-fluid">
    <div class="row">
      <div class="col-sm-12 col-xl-12" style="margin-top: 20px;">
        <div class="card">
          <div class="card-header d-flex align-items-start justify-content-between gap-2 flex-wrap">
            <div>
              <h3 class="mb-1">{{ pageTitle }}</h3>
              <span class="reconciliation-subtitle">CFDIs de ingreso con método de pago PPD</span>
            </div>
            <div class="btn-group af-chip-group status-filter-inline" role="group" aria-label="Filtro por estado">
              <button
                type="button"
                class="btn btn-sm"
                :class="statusFilter === 'all' ? 'btn-primary' : 'btn-light'"
                @click="applyStatusFilter('all')"
              >Todos</button>
              <button
                type="button"
                class="btn btn-sm"
                :class="statusFilter === 'paid' ? 'btn-primary' : 'btn-light'"
                @click="applyStatusFilter('paid')"
              >Pagados</button>
              <button
                type="button"
                class="btn btn-sm"
                :class="statusFilter === 'pending' ? 'btn-primary' : 'btn-light'"
                @click="applyStatusFilter('pending')"
              >Pendientes</button>
            </div>
          </div>

          <div class="card-body position-relative">
            <div class="table-responsive" v-if="rows.length">
              <table class="table table-lg reconciliation-table mb-0">
                <thead>
                  <tr>
                    <th :aria-sort="ariaSortValue('fecha')">
                      <button
                        type="button"
                        class="reconciliation-sort-btn"
                        :class="{ 'reconciliation-sort-btn--active': sortState.key === 'fecha' }"
                        @click="toggleSort('fecha')"
                      >
                        Fecha del CFDI
                        <span class="sort-indicator">{{ sortIndicator('fecha') }}</span>
                      </button>
                    </th>
                    <th :aria-sort="ariaSortValue('folio_uuid')">
                      <button
                        type="button"
                        class="reconciliation-sort-btn"
                        :class="{ 'reconciliation-sort-btn--active': sortState.key === 'folio_uuid' }"
                        @click="toggleSort('folio_uuid')"
                      >
                        Folio / UUID
                        <span class="sort-indicator">{{ sortIndicator('folio_uuid') }}</span>
                      </button>
                    </th>
                    <th :aria-sort="ariaSortValue('counterpart')">
                      <button
                        type="button"
                        class="reconciliation-sort-btn"
                        :class="{ 'reconciliation-sort-btn--active': sortState.key === 'counterpart' }"
                        @click="toggleSort('counterpart')"
                      >
                        {{ counterpartColumnLabel }}
                        <span class="sort-indicator">{{ sortIndicator('counterpart') }}</span>
                      </button>
                    </th>
                    <th class="text-end" :aria-sort="ariaSortValue('total')">
                      <button
                        type="button"
                        class="reconciliation-sort-btn reconciliation-sort-btn--right"
                        :class="{ 'reconciliation-sort-btn--active': sortState.key === 'total' }"
                        @click="toggleSort('total')"
                      >
                        Total
                        <span class="sort-indicator">{{ sortIndicator('total') }}</span>
                      </button>
                    </th>
                    <th :aria-sort="ariaSortValue('payments')">
                      <button
                        type="button"
                        class="reconciliation-sort-btn"
                        :class="{ 'reconciliation-sort-btn--active': sortState.key === 'payments' }"
                        @click="toggleSort('payments')"
                      >
                        Pagos Relacionados
                        <span class="sort-indicator">{{ sortIndicator('payments') }}</span>
                      </button>
                    </th>
                    <th class="text-end" :aria-sort="ariaSortValue('saldo_insoluto')">
                      <button
                        type="button"
                        class="reconciliation-sort-btn reconciliation-sort-btn--right"
                        :class="{ 'reconciliation-sort-btn--active': sortState.key === 'saldo_insoluto' }"
                        @click="toggleSort('saldo_insoluto')"
                      >
                        Saldo Insoluto
                        <span class="sort-indicator">{{ sortIndicator('saldo_insoluto') }}</span>
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in sortedRows" :key="item.id || item.uuid">
                    <td>{{ formatDate(item.fecha) }}</td>
                    <td>
                      <div class="folio-uuid-stack">
                        <span class="folio-text">{{ item.serie_folio || '-' }}</span>
                        <button
                          type="button"
                          class="uuid-link-btn"
                          :title="item.uuid ? 'Ver detalle del CFDI' : 'UUID no disponible'"
                          :disabled="!item.uuid"
                          @click="openPreview(item.uuid)"
                        >
                          {{ item.uuid || '-' }}
                        </button>
                      </div>
                    </td>
                    <td>{{ counterpartName(item) }}</td>
                    <td class="text-end">{{ formatCurrency(item.total) }}</td>
                    <td>
                      <div v-if="item.pagos_relacionados && item.pagos_relacionados.length" class="payment-cards-stack">
                        <button
                          type="button"
                          v-for="payment in sortedPayments(item)"
                          :key="`${item.uuid}-${payment.payment_uuid}-${payment.parcialidades}`"
                          class="payment-card payment-card-link"
                          :title="payment.payment_uuid ? 'Ver detalle del CFDI de pago' : 'CFDI de pago sin UUID disponible'"
                          :disabled="!payment.payment_uuid"
                          @click="openPreview(payment.payment_uuid)"
                        >
                          <div class="payment-card-top">
                            <span class="payment-card-amount">{{ formatCurrency(payment.monto_pagado) }}</span>
                          </div>
                          <div class="payment-card-meta">Fecha de pago: {{ formatDate(payment.fecha_pago) }}</div>
                          <div class="payment-card-meta">Fecha de emision: {{ formatDate(payment.fecha_emision) }}</div>
                          <div v-if="shouldShowPartiality(item)" class="payment-card-meta">Parcialidad: {{ paymentParcialidadLabel(payment) }}</div>
                        </button>
                      </div>
                      <span v-else class="text-muted small">Sin pagos relacionados</span>
                    </td>
                    <td class="text-end insoluto-cell">{{ formatCurrency(item.saldo_insoluto) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div v-else-if="!isLoading" class="text-center py-5 text-muted">
              No se encontraron CFDIs de ingreso PPD para el RFC y periodo seleccionados.
            </div>

            <ul class="pagination pagination-sm pagination-primary mt-3 justify-content-end" v-if="paginator.total_pages > 1">
              <li class="page-item">
                <a
                  class="page-link"
                  :style="paginator.current_page === 1 || isLoading ? 'color: gray !important' : ''"
                  href="javascript:void(0)"
                  @click="!isLoading && paginator.current_page > 1 && changePage(paginator.current_page - 1)"
                >Anterior</a>
              </li>
              <li class="page-item" v-for="page in pagesToShow" :key="page">
                <a
                  class="page-link"
                  href="javascript:void(0)"
                  @click="!isLoading && paginator.current_page !== page && changePage(page)"
                >{{ page }}</a>
              </li>
              <li class="page-item">
                <a
                  class="page-link"
                  :style="paginator.current_page === paginator.total_pages || isLoading ? 'color: gray !important' : ''"
                  href="javascript:void(0)"
                  @click="!isLoading && paginator.current_page < paginator.total_pages && changePage(paginator.current_page + 1)"
                >Siguiente</a>
              </li>
            </ul>

            <div v-if="isLoading" class="reconciliation-loading-overlay">
              <div class="spinner-border text-primary" role="status" aria-hidden="true"></div>
              <div class="reconciliation-loading-text">Consultando conciliación de pagos PPD...</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <CfdiPreviewModal
    :visible="cfdiPreviewModal.visible"
    :uuid="cfdiPreviewModal.uuid"
    :rfc="rfc"
    @close="closePreview"
  />
</div>
</template>

<script>
import CfdiPreviewModal from '@/components/cfdis/CfdiPreviewModal.vue';
import ws from '@/services/cfdis';
import { GLOBAL_PERIOD_EVENT, getGlobalPeriod } from '@/services/globalPeriod';

export default {
  name: 'PaymentReconciliation',
  components: {
    CfdiPreviewModal,
  },
  data() {
    return {
      rfc: '',
      period: '',
      rows: [],
      isLoading: false,
      statusFilter: 'all',
      paginator: {
        current_page: 1,
        per_page: 30,
        total_pages: 1,
        total_items: 0,
      },
      cfdiPreviewModal: {
        visible: false,
        uuid: '',
      },
      sortState: {
        key: 'fecha',
        direction: 'asc',
      },
    };
  },
  computed: {
    nature() {
      const value = String(this.$route?.meta?.paymentReconciliationNature || '').trim().toLowerCase();
      return value === 'received' ? 'received' : 'issued';
    },
    pageTitle() {
      return this.nature === 'issued'
        ? 'Conciliación de Pagos (Emitidos)'
        : 'Conciliación de Pagos (Recibidos)';
    },
    counterpartColumnLabel() {
      return this.nature === 'issued' ? 'Nombre del Receptor' : 'Nombre del Emisor';
    },
    pagesToShow() {
      const total = this.paginator.total_pages;
      const current = this.paginator.current_page;
      const delta = 2;

      if (total === 0) return [];

      let start = current - delta;
      let end = current + delta;

      if (start < 1) {
        end = Math.min(end + (1 - start), total);
        start = 1;
      }

      if (end > total) {
        start = Math.max(1, start - (end - total));
        end = total;
      }

      const pages = [];
      for (let i = start; i <= end; i += 1) {
        pages.push(i);
      }

      return pages;
    },
    sortedRows() {
      const items = Array.isArray(this.rows) ? [...this.rows] : [];
      const directionFactor = this.sortState.direction === 'desc' ? -1 : 1;

      return items.sort((left, right) => {
        const leftValue = this.sortValue(left, this.sortState.key);
        const rightValue = this.sortValue(right, this.sortState.key);

        const leftIsNumber = typeof leftValue === 'number' && Number.isFinite(leftValue);
        const rightIsNumber = typeof rightValue === 'number' && Number.isFinite(rightValue);

        if (leftIsNumber && rightIsNumber) {
          if (leftValue === rightValue) {
            return this.stableRowTieBreak(left, right);
          }
          return (leftValue - rightValue) * directionFactor;
        }

        const leftText = String(leftValue || '').trim();
        const rightText = String(rightValue || '').trim();
        const textDiff = leftText.localeCompare(rightText, 'es', { sensitivity: 'base', numeric: true });

        if (textDiff === 0) {
          return this.stableRowTieBreak(left, right);
        }

        return textDiff * directionFactor;
      });
    },
  },
  watch: {
    '$route.meta.paymentReconciliationNature'() {
      this.paginator.current_page = 1;
      this.refreshItems();
    },
  },
  mounted() {
    this.rfc = String(localStorage.getItem('rfc') || '').trim();
    this.period = getGlobalPeriod();

    this.refreshItems();

    window.addEventListener(GLOBAL_PERIOD_EVENT, this.handleGlobalPeriodChanged);
    window.addEventListener('rfcs-storage-updated', this.handleRfcsUpdated);
  },
  beforeUnmount() {
    window.removeEventListener(GLOBAL_PERIOD_EVENT, this.handleGlobalPeriodChanged);
    window.removeEventListener('rfcs-storage-updated', this.handleRfcsUpdated);
  },
  methods: {
    formatDate(value) {
      if (!value) return '-';
      return typeof this.$formatDate === 'function' ? this.$formatDate(value) : String(value);
    },
    formatCurrency(value) {
      const amount = Number(value || 0);
      return amount.toLocaleString('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    counterpartName(item) {
      if (!item || typeof item !== 'object') return '-';
      return this.nature === 'issued'
        ? String(item.nombre_receptor || '-').trim() || '-'
        : String(item.nombre_emisor || '-').trim() || '-';
    },
    paymentParcialidadLabel(payment) {
      const partialities = String(payment?.parcialidades || '').trim();
      if (partialities) return partialities;

      const partiality = Number(payment?.parcialidad || 0);
      return partiality > 0 ? String(partiality) : '-';
    },
    shouldShowPartiality(item) {
      const payments = Array.isArray(item?.pagos_relacionados) ? item.pagos_relacionados : [];
      if (payments.length > 1) {
        return true;
      }

      const saldoInsoluto = Number(item?.saldo_insoluto || 0);
      return saldoInsoluto > 0.009;
    },
    sortedPayments(item) {
      const payments = Array.isArray(item?.pagos_relacionados) ? [...item.pagos_relacionados] : [];

      return payments.sort((a, b) => {
        const aDate = new Date(a?.fecha_pago || a?.fecha_emision || 0).getTime();
        const bDate = new Date(b?.fecha_pago || b?.fecha_emision || 0).getTime();

        if (aDate === bDate) {
          return String(a?.payment_uuid || '').localeCompare(String(b?.payment_uuid || ''));
        }

        return aDate - bDate;
      });
    },
    sortValue(item, key) {
      switch (key) {
        case 'fecha':
          return new Date(item?.fecha || 0).getTime();
        case 'folio_uuid':
          return `${String(item?.serie_folio || '').trim()} ${String(item?.uuid || '').trim()}`.trim();
        case 'counterpart':
          return this.counterpartName(item);
        case 'total':
          return Number(item?.total || 0);
        case 'payments': {
          const payments = Array.isArray(item?.pagos_relacionados) ? item.pagos_relacionados : [];
          if (!payments.length) return -1;

          const earliestPaymentDate = payments.reduce((minimum, payment) => {
            const current = new Date(payment?.fecha_pago || payment?.fecha_emision || 0).getTime();
            return current < minimum ? current : minimum;
          }, Number.MAX_SAFE_INTEGER);

          return earliestPaymentDate;
        }
        case 'saldo_insoluto':
          return Number(item?.saldo_insoluto || 0);
        default:
          return String(item?.uuid || '').trim();
      }
    },
    stableRowTieBreak(left, right) {
      return String(left?.uuid || '').localeCompare(String(right?.uuid || ''));
    },
    toggleSort(nextKey) {
      const normalized = String(nextKey || '').trim();
      if (!normalized) return;

      if (this.sortState.key === normalized) {
        this.sortState.direction = this.sortState.direction === 'asc' ? 'desc' : 'asc';
        return;
      }

      this.sortState.key = normalized;
      this.sortState.direction = 'asc';
    },
    sortIndicator(key) {
      if (this.sortState.key !== key) return '↕';
      return this.sortState.direction === 'asc' ? '↑' : '↓';
    },
    ariaSortValue(key) {
      if (this.sortState.key !== key) return 'none';
      return this.sortState.direction === 'asc' ? 'ascending' : 'descending';
    },
    async refreshItems() {
      if (!this.rfc || !this.period) {
        this.rows = [];
        this.paginator = {
          current_page: 1,
          per_page: 30,
          total_pages: 1,
          total_items: 0,
        };
        return;
      }

      this.isLoading = true;

      try {
        const response = await ws.getPpdPaymentReconciliation(
          this.rfc,
          this.period,
          this.nature,
          this.paginator.current_page,
          {
            payment_status: this.statusFilter,
          }
        );

        if (response && response.type === 'success') {
          this.rows = Array.isArray(response.data) ? response.data : [];

          const apiPaginator = response.paginator || {};
          this.paginator = {
            current_page: Number(apiPaginator.current_page || this.paginator.current_page || 1),
            per_page: Number(apiPaginator.per_page || 30),
            total_pages: Number(apiPaginator.total_pages || 1),
            total_items: Number(apiPaginator.total_items || 0),
          };
        } else {
          this.rows = [];
        }
      } catch (error) {
        this.rows = [];
      } finally {
        this.isLoading = false;
      }
    },
    changePage(page) {
      const nextPage = Number(page || 1);
      if (nextPage < 1 || nextPage === this.paginator.current_page) {
        return;
      }

      this.paginator.current_page = nextPage;
      this.refreshItems();
    },
    applyStatusFilter(nextStatus) {
      const allowed = ['all', 'paid', 'pending'];
      const normalized = allowed.includes(String(nextStatus || '').toLowerCase())
        ? String(nextStatus).toLowerCase()
        : 'all';

      this.statusFilter = normalized;
      this.paginator.current_page = 1;
      this.refreshItems();
    },
    handleGlobalPeriodChanged(event) {
      const nextPeriod = String(event?.detail?.period || '').trim() || getGlobalPeriod();
      if (!nextPeriod || nextPeriod === this.period) {
        return;
      }

      this.period = nextPeriod;
      this.paginator.current_page = 1;
      this.refreshItems();
    },
    handleRfcsUpdated() {
      const nextRfc = String(localStorage.getItem('rfc') || '').trim();
      if (nextRfc === this.rfc) {
        return;
      }

      this.rfc = nextRfc;
      this.paginator.current_page = 1;
      this.refreshItems();
    },
    openPreview(uuid) {
      const normalized = String(uuid || '').trim().toUpperCase();
      if (!normalized) {
        return;
      }

      this.cfdiPreviewModal.uuid = normalized;
      this.cfdiPreviewModal.visible = true;
    },
    closePreview() {
      this.cfdiPreviewModal.visible = false;
      this.cfdiPreviewModal.uuid = '';
    },
  },
};
</script>

<style scoped>
.reconciliation-subtitle {
  font-size: 12px;
  color: #6c757d;
}

.status-filter-inline {
  white-space: nowrap;
}

.status-filter-inline .btn {
  border-color: var(--theme-deafult) !important;
}

.status-filter-inline .btn + .btn {
  margin-left: 0 !important;
  border-left-width: 0 !important;
}

.reconciliation-table th,
.reconciliation-table td {
  font-size: 11px;
  white-space: nowrap;
  vertical-align: top;
}

.reconciliation-table thead th {
  font-weight: 700;
}

.reconciliation-sort-btn {
  width: 100%;
  border: 0;
  padding: 0;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  font-size: 11px;
  font-weight: 700;
  color: inherit;
  line-height: 1.25;
  text-align: left;
}

.reconciliation-sort-btn--right {
  justify-content: flex-end;
  text-align: right;
}

.reconciliation-sort-btn--active {
  color: var(--theme-deafult);
}

.sort-indicator {
  display: inline-block;
  min-width: 10px;
  font-size: 10px;
}

.uuid-text {
  font-family: monospace;
  font-size: 10px;
}

.uuid-link-btn {
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0;
  text-align: left;
  font-family: monospace;
  font-size: 10px;
  color: var(--theme-deafult);
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 2px;
}

.uuid-link-btn:disabled {
  color: #6c757d;
  text-decoration: none;
}

.folio-uuid-stack {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.folio-text {
  font-size: 11px;
  font-weight: 600;
  color: var(--theme-font-color);
}

.payment-cards-stack {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 210px;
  max-width: 230px;
}

.payment-card {
  border: 1px solid #dbe6ff;
  background: #f7faff;
  border-radius: 8px;
  padding: 6px 8px;
  width: 100%;
}

.payment-card-link {
  text-align: left;
  cursor: pointer;
  transition: border-color .15s ease, box-shadow .15s ease, background-color .15s ease;
}

.payment-card-link:hover:not(:disabled),
.payment-card-link:focus-visible {
  border-color: #9bbcff;
  box-shadow: 0 0 0 2px rgba(115, 102, 255, 0.18);
  background: #eef4ff;
}

.payment-card-link:disabled {
  cursor: default;
  opacity: .78;
}

.payment-card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 2px;
}

.payment-card-amount {
  font-weight: 700;
  font-size: 11px;
  color: #0f766e;
}

.payment-card-meta {
  font-size: 10px;
  color: #425466;
  line-height: 1.35;
}

.insoluto-cell {
  font-weight: 700;
}

.reconciliation-loading-overlay {
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.78);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.reconciliation-loading-text {
  font-size: 12px;
  color: var(--theme-font-color);
}

@media (max-width: 768px) {
  .reconciliation-subtitle {
    font-size: 11px;
  }
}
</style>
